import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import DefaultApiLimit from '../../../../../config/defaultLimit';
import {
  RequestTransactionsInterFace,
  TransactionsInterface,
} from '../../../../../interface/transactions-station-interface';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { setNotificationMessage } from '../../../../../redux/slices/NotificationManagementSlice';
import {
  disableTransactionFiltersApplied,
  fetchTransactions,
  fetchTransactionsFilters,
  updateTransactionsFilters,
  setSortData,
  refreshTransactions,
} from '../../../../../redux/slices/TransactionsStationSlice';
import CustomTable from '../../../../../shared-components/custom-table/CustomTable';
import SidePaneModel from '../../../../../shared-components/table-side-panel/SidePaneModel';
import TransactionSidePanel from '../../../../../shared-components/table-side-panel/TransactionSidePanel';
import { GetParametersFromCommonFilters } from '../../../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../../../shared-functions/ConvertUTCtoDateTime';
import { exportCSV, exportPDF } from '../../../../../shared-functions/ExportFile';
import ToFixedValue from '../../../../../shared-functions/ToFixedValue';
import stationManagementApi from '../../../../../utils/api/station-management';

import TransactionCharts from './TransactionCharts';

/**
 * Transaction component
 * @returns {any} The rendered component
 */
function Transactions() {
  const { t, i18n } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [isTransactionPanelOpen, setIsTransactionPanelOpen] = useState<boolean>(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
  const {
    transactions,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.transactions);
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);

  const column = useMemo(
    () => [
      {
        label: t('charger'),
        accessorKey: 'chargingPointSn' as keyof TransactionsInterface,
        /**
         * Rendering charger
         * @param {TransactionsInterface} currentRow - Passing as props
         * @returns {any} Returning charger element
         */
        customData: (currentRow: TransactionsInterface) => (
          <span>{currentRow.chargingPointSn}</span>
        ),
      },
      {
        label: t('connector'),
        accessorKey: 'connector' as keyof TransactionsInterface,
        /**
         * Rendering connector
         * @param {TransactionsInterface} currentRow - Passing as props
         * @returns {any} Returning connector element
         */
        customData: (currentRow: TransactionsInterface) => (
          <span>{currentRow.cdrLocation.evseUid}</span>
        ),
      },
      {
        label: t('amount'),
        accessorKey: 'inclVat' as keyof TransactionsInterface,
        sortAllow: true,
        /**
         * Rendering amount
         * @param {TransactionsInterface} currentRow - The currentRow
         * @returns {any} Returning amount element
         */
        customData: (currentRow: TransactionsInterface) => {
          return <p>{ToFixedValue(currentRow.totalCost.inclVat)}</p>;
        },
      },
      {
        label: t('energyConsumed'),
        accessorKey: 'powerConsumed' as keyof TransactionsInterface,
        sortAllow: true,
        /**
         * Rendering energy consumed
         * @param {TransactionsInterface} currentRow - The currentRow
         * @returns {any} Returning energy consumed element
         */
        customData: (currentRow: TransactionsInterface) => {
          return <span>{currentRow.totalEnergy}</span>;
        },
      },
    ],
    [t],
  );

  /**
   * Export data function for pdf and csv based on visible columns
   * @param {string} type - Format type ('csv' | 'pdf')
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const query: Record<string, any> = {
      ...queryParameters,
      chargingStation: selectedChargingPoint?.id,
    };
    if (sortBy && sortField) {
      query.sortBy = sortBy;
      query.sortField = sortField;
    }
    setExportLoader(true);
    stationManagementApi
      .fetchTransactionListExport(query)
      .then((response) => {
        const visibleColumns = column.filter(
          (col) => !hiddenColumns.includes(col.accessorKey as string),
        );

        const headers = [
          t('sNo', 'S.No'),
          ...visibleColumns.map((col) => col.label),
          ...(!hiddenColumns.includes('createdAt') ? [t('created', 'Created')] : []),
          ...(!hiddenColumns.includes('updatedAt') ? [t('lastUpdated', 'Last Updated')] : []),
        ];

        const rows = response.data.map((row: TransactionsInterface, index: number) => [
          index + 1,
          ...visibleColumns.map((col) => {
            const accessor = col.accessorKey as string;
            if (accessor === 'chargingPointSn' || accessor === 'charger') {
              return row.chargingPointSn || (row as any).chargingPoint || '-';
            }
            if (accessor === 'connector') {
              return (
                row.cdrLocation?.connectorId ??
                row.cdrLocation?.evseUid ??
                (row as any).connector ??
                '-'
              );
            }
            if (
              accessor === 'inclVat' ||
              accessor === 'totalCost' ||
              accessor === 'amount' ||
              accessor === 'foRevenue'
            ) {
              const amount =
                row.totalCost?.inclVat ??
                row.totalCost?.exclVat ??
                (row as any).foRevenue ??
                (row as any).amount ??
                0;
              return ToFixedValue(amount);
            }
            if (
              accessor === 'powerConsumed' ||
              accessor === 'totalEnergy' ||
              accessor === 'energyConsumed'
            ) {
              const energy =
                row.totalEnergy ??
                (row as any).energyConsumed ??
                (row as any).powerConsumed ??
                0;
              return `${ToFixedValue(energy, 3)} kWh`;
            }
            return (row as any)[accessor] || '-';
          }),
          ...(!hiddenColumns.includes('createdAt')
            ? [ConvertUTCtoDateTime(row.createdAt) || '-']
            : []),
          ...(!hiddenColumns.includes('updatedAt')
            ? [ConvertUTCtoDateTime((row as any).updatedAt) || '-']
            : []),
        ]);

        if (type === 'csv') {
          exportCSV(headers, rows, 'station-transactions.csv');
        } else {
          exportPDF(headers, rows, 'station-transactions.pdf');
        }
        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('somethingWentWrong'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handle the fetch the data in api function
   * @param {any} data The request data
   */
  const fetchData = (data: RequestTransactionsInterFace) => {
    if (!loading && selectedChargingPoint) {
      dispatch(fetchTransactions({ ...data, chargingStation: selectedChargingPoint?.id }));
    }
  };
  /**
   * Table pagination function
   * @param {any} value The pagination value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...value,
      ...queryParameters,
      sortBy,
      sortField,
    });
  };
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortBy, sortField });
  };

  /**
   * Function for sorting table data
   * @param {any} sortData The sort data
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Handle the auto refresh function for transaction list
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      refreshTransactions({
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
        chargingStation: selectedChargingPoint?.id || '',
      }),
    );
  };

  /**
   * Handle row click to open the transaction side panel
   * @param {string} id The id of the row
   * @param {any} row The row data
   */
  const handleOpenSidePanelOnRowClick = (id: string, row: any) => {
    setSelectedTransactionId(row._id || row.id);
    setIsTransactionPanelOpen(true);
  };

  useEffect(() => {
    if (isFilterUpdated) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        ...queryParameters,
        page: DefaultApiLimit.page,
        limit,
      });
      dispatch(disableTransactionFiltersApplied());
    }
  }, [filters, selectedChargingPoint]);

  useEffect(() => {
    if (!selectedChargingPoint?.id) return;

    const currentId = selectedChargingPoint.id;
    const lastId = sessionStorage.getItem('TransactionsFiltersList'); // string | null

    if (lastId !== currentId || filters.length === 1) {
      dispatch(fetchTransactionsFilters({ chargingStation: currentId }));
    }

    if (transactions.length === 0 || lastId !== currentId) {
      fetchData({
        page: DefaultApiLimit.page,
        limit,
      });
      dispatch(disableTransactionFiltersApplied());
    }
    sessionStorage.setItem('TransactionsFiltersList', currentId);
  }, [selectedChargingPoint?.id, filters]);
  return (
    <div className="transaction-wrap" dir={i18n.dir()}>
      <div className="transaction-right-content">
        <CustomTable
          header={column}
          data={transactions}
          serialNo
          version="v2"
          sizeChangeButtonRequired
          limit={limit}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          loading={loading}
          filterListLoading={filterListLoading}
          filters={filters}
          filtersToDispatch={updateTransactionsFilters}
          filterListToDispatch={fetchTransactionsFilters}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportLoader={exportLoader}
          exportData={transactions}
          refershCurrentPages={handleRefresh}
          requiredRefreshToggle
          refreshFunction={handleAutoRefresh}
          pageLimitChange={pageNateLimitChange}
          sortData={{
            sortBy,
            sortField,
          }}
          handleSortChange={handleSortFunction}
          onHiddenColumnsChange={setHiddenColumns}
          rowClickEvent={handleOpenSidePanelOnRowClick}
          height={400}
          created
          lastUpdated
        />
      </div>
      <div className="transaction-left-content">
        <TransactionCharts />
      </div>
      <SidePaneModel
        open={isTransactionPanelOpen}
        closePanel={() => {
          setIsTransactionPanelOpen(false);
          setSelectedTransactionId(null);
        }}
        content={<TransactionSidePanel fetchIdFrom="props" id={selectedTransactionId || ''} />}
        position="right"
        title=" "
      />
    </div>
  );
}

export default Transactions;
